import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "../../../lib/http";
import { API } from "../../../config/api";

const fmtInr = (n = 0) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function AdminBookingsList() {
  const navigate = useNavigate();

  // data
  const [events, setEvents] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // filters
  const [q, setQ] = useState("");
  const [eventId, setEventId] = useState("");
  const [status, setStatus] = useState("");          // Paid / Pending / Failed / Refunded (if supported)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // pagination & sort
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState("-createdAt");    // newest first

  const [loading, setLoading] = useState(false);

  // fetch events (for filter)
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const data = await get(API.events.getEvent || API.events.list || "/api/events", {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }).catch(() => ({ events: [] }));
        if (!on) return;
        setEvents(data?.events || data || []);
      } catch {
        if (on) setEvents([]);
      }
    })();
    return () => { on = false; };
  }, []);

  // build query params (force paymentMethod=cash)
  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set("paginate", "true");
    p.set("page", String(page));
    p.set("limit", String(limit));
    if (sort) p.set("sort", sort);
    if (eventId) p.set("eventId", eventId);
    if (status) p.set("paymentStatus", status);
    if (dateFrom) p.set("from", dateFrom);
    if (dateTo) p.set("to", dateTo);
    if (q) p.set("q", q);
    p.set("paymentMethod", "cash");        // <<< CASH ONLY
    return p.toString();
  }, [page, limit, sort, eventId, status, dateFrom, dateTo, q]);

  // fetch bookings
  const fetchRows = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url = (API.Payment.listCashBooking || "/api/admin/bookings") + "?" + queryParams;
      const resp = await get(url, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      // Expected: { success, paginate, page, limit, total, totalPages, data:[...] }
      setRows(resp?.data || []);
      setTotal(resp?.total || 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const totalPages = Math.max(Math.ceil(total / limit), 1);

  const resetFilters = () => {
    setQ("");
    setEventId("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    setSort("-createdAt");
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cash Bookings</h1>
          <p className="text-sm text-slate-600">Search, filter and manage cash bookings only</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/admin/cash-booking")}
            className="rounded-xl border px-4 py-2 text-sm"
          >
            New Cash Booking
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border bg-white p-4">
        <div className="grid gap-3 md:grid-cols-6">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-600">Search</label>
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Name / Email / Mobile / Booking Code"
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600">Event</label>
            <select
              value={eventId}
              onChange={(e) => { setEventId(e.target.value); setPage(1); }}
              className="mt-1 w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-300"
            >
              <option value="">All</option>
              {events.map((ev) => (
                <option key={ev._id || ev.id} value={ev._id || ev.id}>
                  {ev.name || ev.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600">Status</label>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="mt-1 w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-300"
            >
              <option value="">All</option>
              <option value="Paid">Paid</option>
              <option value="Pending">Pending</option>
              <option value="Failed">Failed</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs text-slate-500">Sorted by: {sort || "(none)"} • Method: cash</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSort(sort === "-createdAt" ? "createdAt" : "-createdAt")}
              className="rounded-full border px-3 py-1.5 text-xs hover:bg-slate-50"
            >
              Toggle Newest/Oldest
            </button>
            <button onClick={() => resetFilters()} className="rounded-full border px-3 py-1.5 text-xs hover:bg-slate-50">
              Reset
            </button>
            <button onClick={() => fetchRows()} className="rounded-full border px-3 py-1.5 text-xs hover:bg-slate-50">
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b bg-slate-50 text-slate-700">
              <th className="px-4 py-2 text-left">Booking</th>
              <th className="px-4 py-2 text-left">Event</th>
              <th className="px-4 py-2 text-left">Buyer</th>
              <th className="px-4 py-2 text-left">Assignee</th>  {/* NEW */}
              <th className="px-4 py-2 text-left">Tickets</th>
              <th className="px-4 py-2 text-left">Amount</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Created</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-slate-500">
                  No bookings found
                </td>
              </tr>
            )}

            {rows.map((b) => {
              const totalTickets = (b?.ticketCategories || []).reduce(
                (s, c) => s + Number(c.finalQuantity || 0),
                0
              );
              const assg = b?.assignedBy || b?.assignee || null; // backend snapshot key
              return (
                <tr
                  key={b._id}
                  className="border-t hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/admin/bookings/${b._id}`)}
                >
                  <td className="px-4 py-2">
                    <div className="font-semibold text-slate-900">{b.bookingCode || b._id}</div>
                    <div className="text-xs text-slate-500">{b.paymentMode}</div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-slate-900">{b?.eventId?.name || "-"}</div>
                    <div className="text-xs text-slate-500">
                      {b?.eventId?.date ? new Date(b.eventId.date).toLocaleString() : "-"}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-slate-900">{b?.buyer?.name || b?.userId?.name || "-"}</div>
                    <div className="text-xs text-slate-500">
                      {b?.buyer?.email || b?.userId?.email || b?.buyer?.mobile || b?.userId?.mobile || "-"}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-slate-900">{assg?.name || "-"}</div>
                    <div className="text-xs text-slate-500">
                      {assg?.email || assg?.mobile || "-"}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    {totalTickets}
                    <div className="text-xs text-slate-500">
                      {(b?.ticketCategories || []).map((c) => `${c.categoryId}:${c.finalQuantity}`).join(", ")}
                    </div>
                  </td>
                  <td className="px-4 py-2">{fmtInr(b?.finalAmount || 0)}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ring-1 ${
                        (b?.paymentStatus === "Paid" && "bg-emerald-50 text-emerald-700 ring-emerald-200") ||
                        (b?.paymentStatus === "Pending" && "bg-amber-50 text-amber-700 ring-amber-200") ||
                        (b?.paymentStatus === "Failed" && "bg-rose-50 text-rose-700 ring-rose-200") ||
                        "bg-slate-50 text-slate-700 ring-slate-200"
                      }`}
                    >
                      {b?.paymentStatus || "-"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {b?.createdAt ? new Date(b.createdAt).toLocaleString() : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <div className="text-xs text-slate-600">
            Page {page} / {totalPages} • {total} results
          </div>
          <div className="flex items-center gap-2">
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
              className="rounded-lg border px-2 py-1 text-xs"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </select>
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className={`rounded-lg border px-3 py-1.5 text-xs ${page <= 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"}`}
            >
              Prev
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className={`rounded-lg border px-3 py-1.5 text-xs ${page >= totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"}`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

